import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import ErrorBoundary from './ErrorBoundary';
import SessionExpiredNotice from '~/components/auth/SessionExpiredNotice';
import GettingStartedPage from '~/screens/docs/GettingStartedPage';
import FormsDocPage from '~/screens/docs/FormsDocPage';
import {
  TbitInvoicesLayout, TbitInvoicesLandingPage, TbitInvoicesConnectPage,
  TbitInvoicesDashboardPage, InvoiceDetailPage,
  TbitInvoicesUploadPage, TbitInvoicesMembersPage,
  TbitInvoicesGmailPickerPage, MemoriasPage,
} from '~/screens/tbit-invoices';
import TbitInvoicesAuthCallback from '~/screens/tbit-invoices/TbitInvoicesAuthCallback';
import {
  JobApplyConnectPage, JobApplyCVPage,
  JobApplyDashboardPage, JobApplyPreferencesPage, JobApplyHistoryPage,
  JobApplyLayout, AdminJobsPage, AdminOverviewPage, AdminUsersPage, AdminWorkersPage,
  JobApplyOnboardingPage,
} from '~/screens/job-apply';

// Lazy-load modules with broken dependencies to prevent blocking the whole app
const ConfirmationPage = lazy(() => import('~/screens/calendar/ConfirmationPage'));
const SchedulerErrorPage = lazy(() => import('~/screens/calendar/ErrorPage'));
const WalkInPage = lazy(() => import('~/screens/calendar/WalkInPage'));
const WebBookingPage = lazy(() => import('~/screens/calendar/WebBookingPage'));
const TicketsPublicPage = lazy(() => import('~/screens/tickets/TicketsPublicPage'));
const TicketThanksPage = lazy(() => import('~/screens/tickets/TicketThanksPage'));
const SchedulePage = lazy(() => import('~/screens/calendar/SchedulePage'));
const AppointmentManagePage = lazy(() => import('~/screens/calendar/AppointmentManagePage'));
const DashboardPage = lazy(() => import('~/screens/dashboard/DashboardPage'));
const FormsModule = lazy(() => import('~/screens/forms').then(m => ({ default: () => <m.EmbedWrapper><m.FormPage /></m.EmbedWrapper> })));
const LoginPage = lazy(() => import('~/screens/auth/LoginPage'));
const CallbackPage = lazy(() => import('~/screens/auth/CallbackPage'));
const AuthGuard = lazy(() => import('~/components/auth/AuthGuard'));
const PrivatePage = lazy(() => import('~/screens/private/PrivatePage'));
const SoluagroPortalPage = lazy(() => import('~/screens/soluagro').then(m => ({ default: m.SoluagroPortalPage })));
const CelerAdminGuardLazy = lazy(() => import('~/screens/celer-admin/CelerAdminGuard'));
const CelerAdminLayoutLazy = lazy(() => import('~/screens/celer-admin/CelerAdminLayout'));
const CelerTenantsListPage = lazy(() => import('~/screens/celer-admin/TenantsListPage'));
const CelerPanoramaPage = lazy(() => import('~/screens/celer-admin/PanoramaPage'));
const CelerTenantCreatePage = lazy(() => import('~/screens/celer-admin/TenantCreatePage'));
const CelerTenantDetailPage = lazy(() => import('~/screens/celer-admin/TenantDetailPage'));
const CelerPackActivatePage = lazy(() => import('~/screens/celer-admin/PackActivatePage'));
const CelerPolicyDetailPage = lazy(() => import('~/screens/celer-admin/PolicyDetailPage'));
const CelerPoliciesFeedPage = lazy(() => import('~/screens/celer-admin/PoliciesFeedPage'));
const CelerSessionLogsPage = lazy(() => import('~/screens/celer-admin/SessionLogsPage'));
const CelerPricesPage = lazy(() => import('~/screens/celer-admin/PricesPage'));
const MrfGuardLazy = lazy(() => import('~/screens/calculadora/MrfGuard'));
const CalculadoraPage = lazy(() => import('~/screens/calculadora/CalculadoraPage'));
const MrfLayoutLazy = lazy(() => import('~/screens/mrf/MrfLayout'));
const MrfCarteraPage = lazy(() => import('~/screens/mrf/CarteraPage'));
const MrfClienteDetallePage = lazy(() => import('~/screens/mrf/ClienteDetallePage'));
const MrfCardexPage = lazy(() => import('~/screens/mrf/CardexPage'));
const MrfConsolidadoPage = lazy(() => import('~/screens/mrf/ConsolidadoPage'));
const MrfLiquidacionPage = lazy(() => import('~/screens/mrf/LiquidacionPage'));
const MrfAjustesPage = lazy(() => import('~/screens/mrf/AjustesPage'));
const MrfAccesosPage = lazy(() => import('~/screens/mrf/AccesosPage'));
const CheckoutTestPage = lazy(() =>
  import('~/screens/checkout-test').then((m) => ({ default: m.CheckoutTestPage })),
);

function LazyFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-base-200">
      <span className="loading loading-spinner loading-lg text-primary" />
    </div>
  );
}

function Home() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-base-100">
      <h1 className="text-3xl font-bold text-primary">Web Exapps</h1>
    </div>
  );
}

/**
 * LINK booking page — /calendar/book/:agentId/:personChannelId.
 * Tokenless: scoped to a known customer by raw ids. The agent emits this URL
 * (handleLinkCreate in tbit-response-engine). Renders the standard booking
 * flow; on success it redirects to /calendar/<order_id>.
 */
function BookForCustomerPage() {
  const { agentId, personChannelId } = useParams<{ agentId: string; personChannelId: string }>();
  if (!agentId || !personChannelId) {
    return (
      <div className="min-h-screen bg-base-100 flex items-center justify-center p-4">
        <div className="alert alert-error max-w-md"><span>Enlace de agendamiento inválido</span></div>
      </div>
    );
  }
  return <SchedulePage agentId={agentId} personChannelId={personChannelId} variant="standard" />;
}

export function App() {
  return (
    <ErrorBoundary>
    <BrowserRouter>
      <SessionExpiredNotice />
      <Routes>
        <Route path="/" element={<Home />} />

        {/* Auth */}
        <Route path="/login" element={<Suspense fallback={<LazyFallback />}><LoginPage /></Suspense>} />
        <Route path="/auth/callback" element={<Suspense fallback={<LazyFallback />}><CallbackPage /></Suspense>} />

        {/* Private (authenticated) */}
        <Route element={<Suspense fallback={<LazyFallback />}><AuthGuard /></Suspense>}>
          <Route path="/private" element={<Suspense fallback={<LazyFallback />}><PrivatePage /></Suspense>} />
        </Route>

        {/* Docs (public) */}
        <Route path="/docs/getting-started" element={<GettingStartedPage />} />
        <Route path="/docs/forms" element={<FormsDocPage />} />

        {/* Forms (public) */}
        <Route path="/forms/:rootId" element={<Suspense fallback={<LazyFallback />}><FormsModule /></Suspense>} />

        {/* Dashboard (public, token-based auth) */}
        <Route path="/external/dashboard/:id" element={<Suspense fallback={<LazyFallback />}><DashboardPage /></Suspense>} />

        {/* MRF (autenticada + allowlist de correos). La Calculadora va DENTRO del
            layout: antes vivía fuera y el menú desaparecía al entrar en ella. */}
        <Route element={<Suspense fallback={<LazyFallback />}><AuthGuard /></Suspense>}>
          <Route element={<Suspense fallback={<LazyFallback />}><MrfGuardLazy /></Suspense>}>
            <Route element={<Suspense fallback={<LazyFallback />}><MrfLayoutLazy /></Suspense>}>
              <Route path="/external/calculadora" element={<Suspense fallback={<LazyFallback />}><CalculadoraPage /></Suspense>} />
              <Route path="/external/mrf" element={<Navigate to="/external/mrf/cartera" replace />} />
              <Route path="/external/mrf/cartera" element={<Suspense fallback={<LazyFallback />}><MrfCarteraPage /></Suspense>} />
              <Route path="/external/mrf/cliente/:id" element={<Suspense fallback={<LazyFallback />}><MrfClienteDetallePage /></Suspense>} />
              <Route path="/external/mrf/cardex/:obligacionId" element={<Suspense fallback={<LazyFallback />}><MrfCardexPage /></Suspense>} />
              <Route path="/external/mrf/consolidado" element={<Suspense fallback={<LazyFallback />}><MrfConsolidadoPage /></Suspense>} />
              <Route path="/external/mrf/liquidacion/:clienteId" element={<Suspense fallback={<LazyFallback />}><MrfLiquidacionPage /></Suspense>} />
              <Route path="/external/mrf/ajustes" element={<Suspense fallback={<LazyFallback />}><MrfAjustesPage /></Suspense>} />
              <Route path="/external/mrf/accesos" element={<Suspense fallback={<LazyFallback />}><MrfAccesosPage /></Suspense>} />
            </Route>
          </Route>
        </Route>

        {/* Soluagro Portal (authenticated) */}
        <Route element={<AuthGuard />}>
          <Route path="/external/soluagro" element={<Suspense fallback={<LazyFallback />}><SoluagroPortalPage /></Suspense>} />
        </Route>

        {/* Celer Admin */}
        <Route element={<Suspense fallback={<LazyFallback />}><AuthGuard /></Suspense>}>
          <Route element={<Suspense fallback={<LazyFallback />}><CelerAdminGuardLazy /></Suspense>}>
            <Route element={<Suspense fallback={<LazyFallback />}><CelerAdminLayoutLazy /></Suspense>}>
              <Route path="/celer-admin" element={<Suspense fallback={<LazyFallback />}><CelerPanoramaPage /></Suspense>} />
              <Route path="/celer-admin/tenants" element={<Suspense fallback={<LazyFallback />}><CelerTenantsListPage /></Suspense>} />
              <Route path="/celer-admin/tenants/new" element={<Suspense fallback={<LazyFallback />}><CelerTenantCreatePage /></Suspense>} />
              <Route path="/celer-admin/tenants/:id" element={<Suspense fallback={<LazyFallback />}><CelerTenantDetailPage /></Suspense>} />
              <Route path="/celer-admin/tenants/:id/packs/new" element={<Suspense fallback={<LazyFallback />}><CelerPackActivatePage /></Suspense>} />
              <Route path="/celer-admin/policies" element={<Suspense fallback={<LazyFallback />}><CelerPoliciesFeedPage /></Suspense>} />
              <Route path="/celer-admin/policies/:id" element={<Suspense fallback={<LazyFallback />}><CelerPolicyDetailPage /></Suspense>} />
              <Route path="/celer-admin/session-logs" element={<Suspense fallback={<LazyFallback />}><CelerSessionLogsPage /></Suspense>} />
              <Route path="/celer-admin/precios" element={<Suspense fallback={<LazyFallback />}><CelerPricesPage /></Suspense>} />
            </Route>
          </Route>
        </Route>

        {/* TBit Invoices (OIDC required) */}
        <Route element={<Suspense fallback={<LazyFallback />}><AuthGuard /></Suspense>}>
          <Route path="/tbit-invoices" element={<TbitInvoicesLayout />}>
            <Route index element={<TbitInvoicesLandingPage />} />
            <Route path="connect" element={<TbitInvoicesConnectPage />} />
            <Route path="auth/callback" element={<TbitInvoicesAuthCallback />} />
            <Route path="dashboard" element={<TbitInvoicesDashboardPage />} />
            <Route path="records/:id" element={<InvoiceDetailPage />} />
            <Route path="upload" element={<TbitInvoicesUploadPage />} />
            <Route path="gmail" element={<TbitInvoicesGmailPickerPage />} />
            <Route path="team" element={<TbitInvoicesMembersPage />} />
            <Route path="memorias" element={<MemoriasPage />} />
          </Route>
        </Route>

        {/* Job Apply (OIDC required) */}
        <Route element={<Suspense fallback={<LazyFallback />}><AuthGuard /></Suspense>}>
          <Route path="/job-apply" element={<JobApplyLayout />}>
            <Route index element={<JobApplyDashboardPage />} />
            <Route path="onboarding" element={<JobApplyOnboardingPage />} />
            <Route path="connect" element={<JobApplyConnectPage />} />
            <Route path="cv" element={<JobApplyCVPage />} />
            <Route path="dashboard" element={<JobApplyDashboardPage />} />
            <Route path="preferences" element={<JobApplyPreferencesPage />} />
            <Route path="history" element={<JobApplyHistoryPage />} />
            <Route path="admin/overview" element={<AdminOverviewPage />} />
            <Route path="admin/jobs" element={<AdminJobsPage />} />
            <Route path="admin/users" element={<AdminUsersPage />} />
            <Route path="admin/workers" element={<AdminWorkersPage />} />
          </Route>
        </Route>

        {/* Calendar (public) */}
        <Route path="/calendar" element={<Suspense fallback={<LazyFallback />}><SchedulerErrorPage /></Suspense>} />
        <Route path="/calendar/walk-in/:agentId" element={<Suspense fallback={<LazyFallback />}><WalkInPage /></Suspense>} />
        {/* Desde la web del negocio (sección «Agendar cita»): reserva estándar, sin cliente conocido */}
        <Route path="/calendar/reservar/:agentId" element={<Suspense fallback={<LazyFallback />}><WebBookingPage /></Suspense>} />
        {/* LINK booking — tokenless, scoped to a known customer by raw ids */}
        <Route path="/calendar/book/:agentId/:personChannelId" element={<Suspense fallback={<LazyFallback />}><BookForCustomerPage /></Suspense>} />
        {/* Appointment self-service management (public, capability-gated by order id) */}
        <Route path="/calendar/:id" element={<Suspense fallback={<LazyFallback />}><AppointmentManagePage /></Suspense>} />
        <Route path="/calendar/:scheduleId/confirmation" element={<Suspense fallback={<LazyFallback />}><ConfirmationPage /></Suspense>} />

        {/* Boletas de rifas/eventos (public) — grilla + compra, queda pendiente de confirmación */}
        <Route path="/tickets/:agentId/:sellableId" element={<Suspense fallback={<LazyFallback />}><TicketsPublicPage /></Suspense>} />
        <Route path="/tickets/:agentId/:sellableId/thanks/:number" element={<Suspense fallback={<LazyFallback />}><TicketThanksPage /></Suspense>} />

        {/* Test payment gateway — public, capability-gated by the unguessable transaction_id */}
        <Route path="/checkout/test/:transactionId" element={<Suspense fallback={<LazyFallback />}><CheckoutTestPage /></Suspense>} />
      </Routes>
    </BrowserRouter>
    </ErrorBoundary>
  );
}
